import React, { memo, useCallback, useMemo, useRef, Fragment } from 'react';
import { generateUUID } from '@Sage/ssm-utils';
import { Icon, useTheme } from '@Sage/hoi-poi-ui';
import { useWeb5Context } from 'lib/Web5Context';
import useOnboarding from '../../hooks/useOnboarding';
import { Modal, Table, EditForm, AddForm } from '../';
import { types, TABLES } from '../../../constants.js';

const InviteUsers = ({
    getUsers,
    setUsers,
    userRoles,
    customEmailValidation,
    handleError,
    handleSetCount,
    productModeCode,
}) => {
    const modalInputRef = useRef(null);
    const { t } = useWeb5Context();
    const theme = useTheme();
    const {
        state,
        dispatch,
        isRequired,
        isEmail,
        isDuplicatedEmail,
        handleDelete,
        handleEdit,
        handleEditFormChange,
        formHasErrors,
        userManagement,
    } = useOnboarding({
        customEmailValidation,
        userRoles,
        getData: () => getUsers(productModeCode),
        getDataCallback: (users) =>
            users.reduce((userArr, { email, role, status, id }) => {
                const user = {
                    email,
                    roleId: role?.id,
                    role: role?.value,
                    status,
                    id,
                };

                if (typeof id !== 'number') {
                    user.id = generateUUID();
                    user.temp = true;
                }

                return [...userArr, user];
            }, []),
        handleError,
        handleSetCount: (value) => handleSetCount(TABLES.INVITED_USERS, value),
        setData: (data) => setUsers(productModeCode, data),
        parseData: (users) =>
            users.reduce(
                (arr, { roleId, status, role, ...otherFields }) => [
                    ...arr,
                    { role: roleId, status: status || 0, ...otherFields },
                ],
                [],
            ),
    });

    const {
        roleOptions,
        handleAddFormChange,
        handleAddFormBlur,
        handleAddFormSave,
        handleEditFormBlur,
        handleEditFormSave,
        getAdvice,
    } = userManagement;

    const formSchema = useMemo(
        () => [
            {
                label: t('label_email'),
                placeholder: t('placeholder_text_field'),
                labelMode: 'vertical',
                name: 'email',
                type: 'text',
                isRequired: true,
                ref: (ref) => (modalInputRef.current = ref),
            },
            {
                label: t('label_invite_members_role'),
                placeholder: t('label_selectone'),
                labelMode: 'vertical',
                name: 'role',
                type: 'select',
                isRequired: true,
                attrs: {
                    options: roleOptions,
                    overrides: {
                        controlFocused: {
                            style: {
                                border: `1px solid ${theme.colors.actionMajor[500]}`,
                            },
                        },
                    },
                },
            },
        ],
        [theme, roleOptions, t],
    );

    const validateField = useCallback(
        (field, value, index) => {
            let error = null;

            switch (field) {
                case 'email':
                    error = isRequired(value) || isEmail(value) || isDuplicatedEmail(value, index);
                    break;
                case 'role':
                    error = isRequired(value);
                    break;
                default:
                    break;
            }

            return error;
        },
        [isRequired, isEmail, isDuplicatedEmail],
    );

    const tableProps = useMemo(() => {
        return {
            title: t('label_invite_members_title'),
            description: t('label_invite_members_title_desc'),
            actionText: t('action_invite_members'),
            actionOnClick: () => dispatch({ type: types.OPEN_MODAL, value: types.MODAL_ADD }),
            columnDefs: [
                {
                    colId: 'email',
                    headerName: t('label_email'),
                    field: 'email',
                    suppressSizeToFit: false,
                    resizable: false,
                },
                {
                    colId: 'role',
                    headerName: t('label_invite_members_role'),
                    field: 'role',
                    suppressSizeToFit: false,
                    resizable: false,
                    cellRendererParams: {
                        align: 'right',
                    },
                },
            ],
            rowData: state.data,
            actions: [
                (row) => {
                    return row.status !== 1
                        ? {
                              icon: (
                                  <Icon
                                      name="edit"
                                      onClick={() =>
                                          handleEdit({
                                              email: row.email,
                                              role: {
                                                  label: row.role,
                                                  value: row.roleId,
                                              },
                                              id: row.id,
                                          })
                                      }
                                  />
                              ),
                              tooltip: { content: t('action_edit') },
                          }
                        : null;
                },
                (row) => {
                    return row.status !== 1
                        ? {
                              icon: <Icon name="trash" onClick={() => handleDelete(row)} />,
                              tooltip: { content: t('action_delete') },
                          }
                        : null;
                },
                (row) => {
                    return row.status === 1
                        ? {
                              icon: <Icon name="lock" />,
                              tooltip: { content: t('cfm_label_readonly') },
                          }
                        : null;
                },
            ],
            loading: state.loading,
            forceActionsRefresh: true,
        };
    }, [t, state.data, state.loading, dispatch, handleEdit, handleDelete]);

    const modalProps = useMemo(() => {
        let customProps = {};
        let form;
        let preComponent = null;

        const commonProps = {
            isOpen: state.modal.isOpen,
            onRequestClose: () => dispatch({ type: types.CLOSE_MODAL }),
            onCancel: () => dispatch({ type: types.CLOSE_MODAL }),
            modalInputRef: modalInputRef,
        };

        switch (state.modal.type) {
            case types.MODAL_EDIT:
                form = state.modal[types.MODAL_EDIT].values;
                preComponent = getAdvice(form);
                customProps = {
                    title: t('label_edit_member'),
                    onConfirm: handleEditFormSave,
                    isConfirmDisabled: formHasErrors(types.MODAL_EDIT),
                    preComponent,
                };
                break;
            case types.MODAL_ADD:
                form = state.modal[types.MODAL_ADD].values;
                form.every((value) => {
                    preComponent = getAdvice(value);
                    return !preComponent;
                });
                customProps = {
                    title: t('label_invite_members'),
                    description: t('label_invite_members_desc'),
                    onConfirm: () => handleAddFormSave(validateField, formSchema),
                    isConfirmDisabled: formHasErrors(types.MODAL_ADD),
                    preComponent,
                };
                break;
            default:
                break;
        }

        return {
            ...commonProps,
            ...customProps,
        };
    }, [
        state.modal,
        dispatch,
        getAdvice,
        t,
        handleEditFormSave,
        formHasErrors,
        handleAddFormSave,
        validateField,
        formSchema,
    ]);

    const modalContent = useMemo(() => {
        let modalContentProps = null;
        let ModalContent = null;

        switch (state.modal.type) {
            case types.MODAL_EDIT:
                modalContentProps = {
                    form: state.modal[types.MODAL_EDIT],
                    onChange: (payload) => handleEditFormChange(payload, validateField),
                    onBlur: handleEditFormBlur,
                    schema: formSchema,
                    modalInputRef,
                };
                ModalContent = EditForm;
                break;
            case types.MODAL_ADD:
                modalContentProps = {
                    form: state.modal[types.MODAL_ADD],
                    onChange: (payload) => handleAddFormChange(payload, validateField, formSchema),
                    onBlur: handleAddFormBlur,
                    schema: formSchema,
                    modalInputRef,
                };
                ModalContent = AddForm;
                break;
            default:
                break;
        }

        if (!ModalContent) return null;

        return <ModalContent {...modalContentProps} />;
    }, [
        state.modal,
        handleEditFormChange,
        handleEditFormBlur,
        formSchema,
        handleAddFormBlur,
        handleAddFormChange,
        validateField,
    ]);

    return (
        <Fragment>
            <Table {...tableProps} />
            <Modal {...modalProps}>{modalContent}</Modal>
        </Fragment>
    );
};

export default memo(InviteUsers);
